import type {
  CalendarAttendee,
  CalendarEvent,
  RSVPStatus,
} from "@dropbox/dash-component-library/";
import { isAfter } from "date-fns";
import dompurify from "dompurify";
import type { CalendarChatJson, MeetingAttendee } from "./types";

export const GOOGLE_CALENDAR_CONNECTION_ERROR_MESSAGE =
  "Could not get gcal connection info";

export const containsErrorMessage = (cardData: CalendarChatJson) => {
  return !!cardData.contextUserError?.message;
};

/**
 * Returns the first error message found if there are multiple error messages
 * @param cardData
 */
export const getErrorMessage = (
  cardData: CalendarChatJson[],
): string | null => {
  let errorMessage = "";

  const hasErrorMessage = cardData.some((card) => {
    if (card.contextUserError?.message) {
      errorMessage = card.contextUserError.message; // future: can concate multiple error messages too
      return true;
    }
    return false;
  });

  return hasErrorMessage ? errorMessage : null;
};

/**
 * Checks if the error message indicates a Google Calendar connection issue
 * @param cardData
 */
export const isGoogleCalendarConnectionError = (
  cardData: CalendarChatJson[],
): boolean => {
  const errorMessage = getErrorMessage(cardData);
  return errorMessage?.trim() === GOOGLE_CALENDAR_CONNECTION_ERROR_MESSAGE;
};

export const isValidCalendarCardJson = (cardData: CalendarChatJson) => {
  return !!cardData.meetingInfo && !!cardData.meetingInfo.id;
};

/**
 *
 * Data conversion
 */
const RSVP_STATUS_MAP: Record<string, RSVPStatus> = {
  accepted: "accepted",
  declined: "declined",
  needsAction: "needsAction",
};

export const hasDeclinedMeeting = (
  attendees: CalendarAttendee[],
  accountEmail?: string,
) => {
  return attendees.some(
    (attendee) =>
      attendee.email === accountEmail && attendee.rsvpStatus === "declined",
  );
};

export const convertValidCalendarCardJsonToCalendarEventData = (
  cardData: CalendarChatJson,
  accountEmail?: string,
): CalendarEvent => {
  const meetingInfo = cardData.meetingInfo;

  const attendees =
    meetingInfo?.attendees?.map((attendee: MeetingAttendee, index: number) => ({
      id: `attendee-${index}`,
      email: attendee.contact.email,
      name:
        attendee.contact.displayName || attendee.contact.email.split("@")[0],
      profileImageUrl: undefined,
      rsvpStatus: RSVP_STATUS_MAP[attendee.rsvpStatus] || "na",
    })) || [];

  const startTime = parseInt(meetingInfo?.startMs || "0", 10);
  const endTime = parseInt(meetingInfo?.endMs || "0", 10);

  return {
    id: meetingInfo?.id || "",
    title: meetingInfo?.summary || "",
    startTime,
    endTime,
    sanitizedDescription: dompurify.sanitize(meetingInfo?.description || ""),
    attendees,
    hasDeclinedEvent: hasDeclinedMeeting(attendees, accountEmail),
    creatorEmail: meetingInfo?.creator,
  };
};

/**
 * Splits events into attending and declined events
 * @param events
 * @returns tuple: [[attending events], [declined events]]
 */
export const splitEventsByStatus = (
  events: CalendarEvent[],
): [CalendarEvent[], CalendarEvent[]] => {
  return events.reduce(
    (acc, event) => {
      // Not explicitly declined will be assumed as an tentatively "accepted" meeting we'd want to show
      if (!event.hasDeclinedEvent) {
        acc[0].push(event);
      } else {
        acc[1].push(event);
      }
      return acc;
    },
    [[], []] as [CalendarEvent[], CalendarEvent[]],
  );
};

export interface CalendarEventTimeInfo {
  eventState: "in_progress" | "upcoming";
  hours: number;
  minutes: number;
}

/**
 * Calculates the time difference between a calendar event start time and current time
 * @param startTime - The event start time in milliseconds
 * @param currentTime - The current time in milliseconds (defaults to Date.now())
 * @returns Object containing event state and time difference in hours and minutes
 */
export const calculateEventTimeInfo = (
  startTime: number,
  currentTime = Date.now(),
): CalendarEventTimeInfo => {
  const ONE_MINUTE_IN_MILLIS = 60_000;
  let eventState: "in_progress" | "upcoming";
  let timeDiffMinutes: number;

  const startTimeDate = new Date(startTime);
  const currentTimeDate = new Date(currentTime);

  if (isAfter(startTimeDate, currentTimeDate)) {
    // Event is upcoming
    eventState = "upcoming";
    timeDiffMinutes = Math.max(
      Math.round((startTime - currentTime) / ONE_MINUTE_IN_MILLIS),
      0,
    );
  } else {
    // Event has started or is in the past
    eventState = "in_progress";
    timeDiffMinutes = Math.max(
      Math.round((currentTime - startTime) / ONE_MINUTE_IN_MILLIS),
      0,
    );
  }

  // Calculate hours and minutes
  let hours = 0;
  let minutes = timeDiffMinutes;

  if (minutes >= 60) {
    hours = Math.floor(minutes / 60);
    minutes = minutes % 60;
  }

  return {
    eventState,
    hours,
    minutes,
  };
};
